import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { api, errText } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Banner, Loading, fmtDate, KV } from '@/components/OpsUI';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Ticket, RefreshCw, Send, StickyNote, Inbox, Radio, Plus, MessageSquare } from 'lucide-react';
import { toast } from 'sonner';
import { useSearchParams } from 'react-router-dom';
import { AiSuggestBox } from '@/components/ai/AiSuggestBox';
import { LearnButton } from '@/components/ai/LearnButton';
import { WaTicketsPanel } from '@/components/tickets/WaTicketsPanel';

const STATUS_STYLES = {
  open: 'bg-blue-500/15 text-blue-300 border-blue-500/30',
  in_progress: 'bg-amber-500/15 text-amber-300 border-amber-500/30',
  resolved: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30',
  closed: 'bg-slate-500/15 text-slate-300 border-slate-500/30',
};
const PRIORITY_STYLES = {
  low: 'bg-slate-500/15 text-slate-300 border-slate-500/30',
  normal: 'bg-sky-500/15 text-sky-300 border-sky-500/30',
  high: 'bg-orange-500/15 text-orange-300 border-orange-500/30',
  urgent: 'bg-red-500/15 text-red-300 border-red-500/30',
};

const STATUS_LABELS = { open: 'Abierto', in_progress: 'En progreso', resolved: 'Resuelto', closed: 'Cerrado' };
const PRIORITY_LABELS = { low: 'Baja', normal: 'Normal', high: 'Alta', urgent: 'Urgente' };

const pick = (obj, keys, fallback = '') => {
  for (const k of keys) {
    const v = k.split('.').reduce((o, p) => (o == null ? o : o[p]), obj);
    if (v !== undefined && v !== null && v !== '') return v;
  }
  return fallback;
};

const EVENT_LABELS = {
  'ticket.created': 'Nuevo ticket',
  'ticket.replied': 'El pastor respondió',
  'ticket.status_changed': 'Cambio de estado',
};

function EventIcon({ event }) {
  if (event === 'ticket.created') return <div className="w-8 h-8 rounded-full bg-blue-500/15 flex items-center justify-center shrink-0"><Plus className="w-4 h-4 text-blue-300" /></div>;
  if (event === 'ticket.replied') return <div className="w-8 h-8 rounded-full bg-emerald-500/15 flex items-center justify-center shrink-0"><MessageSquare className="w-4 h-4 text-emerald-300" /></div>;
  if (event === 'ticket.status_changed') return <div className="w-8 h-8 rounded-full bg-amber-500/15 flex items-center justify-center shrink-0"><RefreshCw className="w-4 h-4 text-amber-300" /></div>;
  return <div className="w-8 h-8 rounded-full bg-slate-500/15 flex items-center justify-center shrink-0"><Radio className="w-4 h-4 text-slate-300" /></div>;
}

function StatusBadge({ value }) {
  if (!value) return <span className="text-[hsl(var(--muted-foreground))]">—</span>;
  return <Badge variant="outline" className={STATUS_STYLES[value] || ''} data-testid={`ticket-status-${value}`}>{STATUS_LABELS[value] || value}</Badge>;
}
function PriorityBadge({ value }) {
  if (!value) return <span className="text-[hsl(var(--muted-foreground))]">—</span>;
  return <Badge variant="outline" className={PRIORITY_STYLES[value] || ''}>{PRIORITY_LABELS[value] || value}</Badge>;
}

export default function TicketsPage() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const canAct = ['admin', 'tech'].includes(user?.role);

  const [meta, setMeta] = useState({ statuses: ['open', 'in_progress', 'resolved', 'closed'], priorities: ['low', 'normal', 'high', 'urgent'] });
  const [tickets, setTickets] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({ status: 'all', church_id: '', assigned_agent: '', since: '' });
  const [offset, setOffset] = useState(0);
  const limit = 25;

  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [reply, setReply] = useState('');
  const [closeOnReply, setCloseOnReply] = useState(false);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [aiSid, setAiSid] = useState(null);
  const [lastSent, setLastSent] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();

  const [waOpen, setWaOpen] = useState(() => searchParams.get('wa_ticket'));
  const [tab, setTab] = useState(() => (searchParams.get('wa_ticket') ? 'whatsapp' : 'tickets'));
  const [events, setEvents] = useState([]);
  const [eventsLoading, setEventsLoading] = useState(true);

  const clearWaOpen = useCallback(() => setWaOpen(null), []);

  const loadEvents = useCallback(async () => {
    setEventsLoading(true);
    try {
      const { data } = await api.get('/tickets/events', { params: { limit: 50 } });
      setEvents(Array.isArray(data) ? data : []);
    } catch { /* noop */ } finally {
      setEventsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (tab !== 'events') return undefined;
    loadEvents();
    const id = setInterval(loadEvents, 15000);
    return () => clearInterval(id);
  }, [tab, loadEvents]);

  useEffect(() => {
    api.get('/tickets/ping').then(({ data }) => {
      const d = data?.data || {};
      if (d.statuses || d.priorities) {
        setMeta({ statuses: d.statuses || meta.statuses, priorities: d.priorities || meta.priorities });
      }
    }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = { offset, limit };
      if (filters.status && filters.status !== 'all') params.status = filters.status;
      if (filters.church_id) params.church_id = filters.church_id;
      if (filters.assigned_agent) params.assigned_agent = filters.assigned_agent;
      if (filters.since) params.since = new Date(filters.since).toISOString();
      const { data } = await api.get('/tickets', { params });
      setTickets(data?.data || data?.tickets || (Array.isArray(data) ? data : []));
      setTotal(data?.total ?? (data?.data || []).length);
    } catch (e) {
      setError(errText(e));
    } finally {
      setLoading(false);
    }
  }, [filters, offset]);

  useEffect(() => { load(); }, [load]);

  const openTicket = async (id) => {
    setOpen(true);
    setDetail(null);
    setDetailLoading(true);
    setReply(''); setNote(''); setCloseOnReply(false); setAiSid(null); setLastSent(null);
    try {
      const { data } = await api.get(`/tickets/${id}`);
      setDetail(data?.ticket || data?.data || data);
    } catch (e) {
      toast.error(errText(e));
      setOpen(false);
    } finally {
      setDetailLoading(false);
    }
  };

  useEffect(() => {
    if (searchParams.get('wa_ticket')) { setSearchParams({}, { replace: true }); return; }
    const id = searchParams.get('ticket');
    if (!id) return;
    setSearchParams({}, { replace: true });
    openTicket(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const refreshDetail = async (id) => {
    try {
      const { data } = await api.get(`/tickets/${id}`);
      setDetail(data?.ticket || data?.data || data);
    } catch { /* noop */ }
  };

  const ticketId = (tk) => pick(tk, ['id', 'ticket_id', '_id']);

  const doRespond = async () => {
    if (!reply.trim()) return;
    setBusy(true);
    try {
      if (aiSid) await api.post(`/ai/suggestions/${aiSid}/send`, { message: reply, close_ticket: closeOnReply });
      else await api.post(`/tickets/${ticketId(detail)}/respond`, { message: reply, close_ticket: closeOnReply });
      toast.success(closeOnReply ? 'Respuesta enviada y ticket cerrado' : 'Respuesta enviada');
      setLastSent({ message: reply, sid: aiSid });
      setReply(''); setCloseOnReply(false); setAiSid(null);
      await refreshDetail(ticketId(detail));
      load();
    } catch (e) { toast.error(errText(e)); } finally { setBusy(false); }
  };

  const doNote = async () => {
    if (!note.trim()) return;
    setBusy(true);
    try {
      await api.post(`/tickets/${ticketId(detail)}/notes`, { note });
      toast.success('Nota interna guardada');
      setNote('');
      await refreshDetail(ticketId(detail));
    } catch (e) { toast.error(errText(e)); } finally { setBusy(false); }
  };

  const doPatch = async (fields) => {
    setBusy(true);
    try {
      await api.patch(`/tickets/${ticketId(detail)}`, fields);
      toast.success('Ticket actualizado');
      await refreshDetail(ticketId(detail));
      load();
    } catch (e) { toast.error(errText(e)); } finally { setBusy(false); }
  };

  const messages = detail ? (detail.responses || detail.messages || detail.thread || detail.conversation || []) : [];
  const notes = detail ? (detail.notes || detail.internal_notes || []) : [];

  return (
    <div className="space-y-6" data-testid="tickets-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Ticket className="w-6 h-6 text-[hsl(var(--primary))]" />
            Tickets de soporte
          </h1>
          <p className="text-sm text-[hsl(var(--muted-foreground))] mt-1">Suite Ministerial y casos derivados de la atención por WhatsApp</p>
        </div>
        <Button variant="outline" size="sm" onClick={() => { load(); loadEvents(); }} data-testid="tickets-refresh">
          <RefreshCw className="w-4 h-4 mr-2" />{t('refresh', 'Actualizar')}
        </Button>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList data-testid="tickets-tabs">
          <TabsTrigger value="tickets" data-testid="tab-tickets">Suite Ministerial</TabsTrigger>
          <TabsTrigger value="whatsapp" data-testid="tab-wa-tickets">WhatsApp</TabsTrigger>
          <TabsTrigger value="events" data-testid="tab-events">Actividad en vivo</TabsTrigger>
        </TabsList>

        <TabsContent value="tickets" className="space-y-6 mt-4">
      {/* Filtros */}
      <Card className="p-4">
        <div className="grid grid-cols-1 md:grid-cols-5 gap-3 items-end">
          <div>
            <label className="text-xs text-[hsl(var(--muted-foreground))]">Estado</label>
            <Select value={filters.status} onValueChange={(v) => { setOffset(0); setFilters((f) => ({ ...f, status: v })); }}>
              <SelectTrigger data-testid="filter-status"><SelectValue placeholder="Todos" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos</SelectItem>
                {meta.statuses.map((s) => <SelectItem key={s} value={s}>{STATUS_LABELS[s] || s}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-xs text-[hsl(var(--muted-foreground))]">Iglesia (ID)</label>
            <Input value={filters.church_id} onChange={(e) => setFilters((f) => ({ ...f, church_id: e.target.value }))} placeholder="church_id" data-testid="filter-church" />
          </div>
          <div>
            <label className="text-xs text-[hsl(var(--muted-foreground))]">Agente asignado</label>
            <Input value={filters.assigned_agent} onChange={(e) => setFilters((f) => ({ ...f, assigned_agent: e.target.value }))} placeholder="agente" data-testid="filter-agent" />
          </div>
          <div>
            <label className="text-xs text-[hsl(var(--muted-foreground))]">Desde</label>
            <Input type="date" value={filters.since} onChange={(e) => setFilters((f) => ({ ...f, since: e.target.value }))} data-testid="filter-since" />
          </div>
          <Button onClick={() => { setOffset(0); load(); }} data-testid="filter-apply">Filtrar</Button>
        </div>
      </Card>

      {error && <Banner kind="error" title="No se pudo cargar los tickets" message={error} />}

      {/* Lista */}
      <Card className="overflow-hidden">
        {loading ? (
          <div className="p-10"><Loading /></div>
        ) : tickets.length === 0 ? (
          <div className="p-12 text-center text-[hsl(var(--muted-foreground))]" data-testid="tickets-empty">
            <Inbox className="w-10 h-10 mx-auto mb-3 opacity-50" />
            <p className="font-medium">No hay tickets con estos filtros</p>
            <p className="text-sm mt-1">Cuando un pastor abra un ticket en la Suite, aparecerá aquí.</p>
          </div>
        ) : (
          <div className="divide-y divide-border">
            {tickets.map((tk) => (
              <button
                key={ticketId(tk)}
                onClick={() => openTicket(ticketId(tk))}
                className="w-full text-left p-4 hover:bg-[hsl(var(--muted))]/30 transition-colors flex items-center gap-4"
                data-testid={`ticket-row-${ticketId(tk)}`}
              >
                <div className="flex-1 min-w-0">
                  <div className="font-medium truncate">{pick(tk, ['subject', 'title', 'summary'], `#${ticketId(tk)}`)}</div>
                  <div className="text-xs text-[hsl(var(--muted-foreground))] mt-0.5 truncate">
                    {pick(tk, ['church_name', 'church.name', 'church_id'], '—')} · {pick(tk, ['user_name', 'pastor_name', 'requester_name', 'created_by', 'author'], 'Pastor')}
                  </div>
                </div>
                <PriorityBadge value={pick(tk, ['priority'], null)} />
                <StatusBadge value={pick(tk, ['status'], null)} />
                <div className="text-xs text-[hsl(var(--muted-foreground))] w-36 text-right hidden lg:block">
                  {fmtDate(pick(tk, ['updated_at', 'last_activity_at', 'created_at'], null))}
                </div>
              </button>
            ))}
          </div>
        )}
      </Card>

      {/* Paginación */}
      {total > limit && (
        <div className="flex items-center justify-between text-sm">
          <span className="text-[hsl(var(--muted-foreground))]">{offset + 1}–{Math.min(offset + limit, total)} de {total}</span>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" disabled={offset === 0} onClick={() => setOffset(Math.max(0, offset - limit))} data-testid="page-prev">Anterior</Button>
            <Button variant="outline" size="sm" disabled={offset + limit >= total} onClick={() => setOffset(offset + limit)} data-testid="page-next">Siguiente</Button>
          </div>
        </div>
      )}
        </TabsContent>

        <TabsContent value="whatsapp" className="mt-4">
          <WaTicketsPanel canAct={canAct} openId={waOpen} onOpened={clearWaOpen} />
        </TabsContent>

        <TabsContent value="events" className="mt-4">
          <Card className="overflow-hidden">
            <div className="flex items-center justify-between p-4 border-b border-border">
              <div className="flex items-center gap-2 text-sm font-medium">
                <Radio className="w-4 h-4 text-emerald-400" />Eventos recibidos por webhook
              </div>
              <Button variant="outline" size="sm" onClick={loadEvents} data-testid="events-refresh">
                <RefreshCw className="w-4 h-4 mr-2" />Actualizar
              </Button>
            </div>
            {eventsLoading ? (
              <div className="p-10"><Loading /></div>
            ) : events.length === 0 ? (
              <div className="p-12 text-center text-[hsl(var(--muted-foreground))]" data-testid="events-empty">
                <Radio className="w-10 h-10 mx-auto mb-3 opacity-50" />
                <p className="font-medium">Aún no llegan eventos</p>
                <p className="text-sm mt-1">Cuando la Suite envíe ticket.created / replied / status_changed, aparecerán aquí.</p>
              </div>
            ) : (
              <div className="divide-y divide-border">
                {events.map((ev) => {
                  const tid = pick(ev, ['payload.data.id', 'payload.ticket_id', 'payload.id'], null);
                  const extra = pick(ev.payload, ['data.subject', 'data.status'], '');
                  return (
                    <div key={ev.id} className="p-4 flex items-center gap-3" data-testid={`event-${ev.id}`}>
                      <EventIcon event={ev.event} />
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium">{EVENT_LABELS[ev.event] || ev.event || 'evento'}</div>
                        <div className="text-xs text-[hsl(var(--muted-foreground))] truncate">
                          {tid ? `Ticket ${tid}` : 'Sin ticket asociado'}{extra ? ` · ${extra}` : ''}
                        </div>
                      </div>
                      <span className="text-xs text-[hsl(var(--muted-foreground))] w-40 text-right hidden lg:block">{fmtDate(ev.received_at)}</span>
                      {tid && <Button size="sm" variant="outline" onClick={() => openTicket(tid)} data-testid={`event-open-${ev.id}`}>Ver</Button>}
                    </div>
                  );
                })}
              </div>
            )}
          </Card>
        </TabsContent>
      </Tabs>

      {/* Detalle */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="ticket-detail-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 pr-6">
              <Ticket className="w-5 h-5 text-[hsl(var(--primary))]" />
              {detail ? pick(detail, ['subject', 'title', 'summary'], `Ticket #${ticketId(detail)}`) : 'Ticket'}
            </DialogTitle>
          </DialogHeader>

          {detailLoading || !detail ? (
            <div className="p-8"><Loading /></div>
          ) : (
            <div className="space-y-5">
              {/* Meta + acciones de estado */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6">
                <KV k="ID" v={ticketId(detail)} />
                <KV k="Iglesia" v={pick(detail, ['church_name', 'church.name', 'church_id'], '—')} />
                <KV k="Pastor" v={pick(detail, ['user_name', 'pastor_name', 'requester_name', 'created_by', 'author'], '—')} />
                <KV k="Categoría" v={pick(detail, ['category'], '—')} />
                <KV k="Creado" v={fmtDate(pick(detail, ['created_at'], null))} />
                <KV k="Actualizado" v={fmtDate(pick(detail, ['updated_at', 'last_activity_at'], null))} />
              </div>

              {canAct ? (
                <Card className="p-3 grid grid-cols-1 md:grid-cols-4 gap-3 items-end bg-[hsl(var(--muted))]/20">
                  <div>
                    <label className="text-xs text-[hsl(var(--muted-foreground))]">Estado</label>
                    <Select value={pick(detail, ['status'], undefined)} onValueChange={(v) => doPatch({ status: v })}>
                      <SelectTrigger data-testid="detail-status"><SelectValue /></SelectTrigger>
                      <SelectContent>{meta.statuses.map((s) => <SelectItem key={s} value={s}>{STATUS_LABELS[s] || s}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="text-xs text-[hsl(var(--muted-foreground))]">Prioridad</label>
                    <Select value={pick(detail, ['priority'], undefined)} onValueChange={(v) => doPatch({ priority: v })}>
                      <SelectTrigger data-testid="detail-priority"><SelectValue /></SelectTrigger>
                      <SelectContent>{meta.priorities.map((p) => <SelectItem key={p} value={p}>{PRIORITY_LABELS[p] || p}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="text-xs text-[hsl(var(--muted-foreground))]">Cambiar categoría</label>
                    <Input defaultValue={pick(detail, ['category'], '')} onBlur={(e) => { const v = e.target.value.trim(); if (v && v !== pick(detail, ['category'], '')) doPatch({ category: v }); }} data-testid="detail-category" />
                  </div>
                  <div>
                    <label className="text-xs text-[hsl(var(--muted-foreground))]">Agente asignado</label>
                    <Input defaultValue={pick(detail, ['assigned_agent'], '')} onBlur={(e) => { const v = e.target.value.trim(); if (v && v !== pick(detail, ['assigned_agent'], '')) doPatch({ assigned_agent: v }); }} data-testid="detail-agent" />
                  </div>
                </Card>
              ) : (
                <div className="flex gap-2"><StatusBadge value={pick(detail, ['status'], null)} /><PriorityBadge value={pick(detail, ['priority'], null)} /></div>
              )}

              {/* Conversación */}
              <div>
                <h3 className="text-sm font-semibold mb-2">Conversación</h3>
                <div className="space-y-3">
                  {messages.length === 0 && <p className="text-sm text-[hsl(var(--muted-foreground))]">Sin mensajes en el hilo.</p>}
                  {messages.map((m, i) => {
                    const isSupport = ['support', 'agent', 'soporte', 'bomm'].includes(String(pick(m, ['role', 'author_type', 'author_role', 'from'], '')).toLowerCase());
                    return (
                      <div key={i} className={`rounded-lg p-3 border ${isSupport ? 'bg-[hsl(var(--primary))]/10 border-[hsl(var(--primary))]/30 ml-6' : 'bg-[hsl(var(--muted))]/20 border-border mr-6'}`} data-testid={`ticket-msg-${i}`}>
                        <div className="flex justify-between text-xs mb-1">
                          <span className="font-medium">{isSupport ? 'Soporte' : 'Pastor'} · {pick(m, ['agent_name', 'author', 'author_name', 'from_name'], '')}</span>
                          <span className="text-[hsl(var(--muted-foreground))]">{fmtDate(pick(m, ['created_at', 'timestamp', 'date'], null))}</span>
                        </div>
                        <div className="text-sm whitespace-pre-wrap">{pick(m, ['message', 'body', 'text', 'content'], '')}</div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Notas internas */}
              {notes.length > 0 && (
                <div>
                  <h3 className="text-sm font-semibold mb-2 flex items-center gap-1.5"><StickyNote className="w-4 h-4 text-amber-400" />Notas internas <span className="text-xs font-normal text-[hsl(var(--muted-foreground))]">(el pastor no las ve)</span></h3>
                  <div className="space-y-2">
                    {notes.map((n, i) => (
                      <div key={i} className="rounded-lg p-3 border border-amber-500/30 bg-amber-500/10" data-testid={`ticket-note-${i}`}>
                        <div className="flex justify-between text-xs mb-1">
                          <span className="font-medium">{pick(n, ['agent_name', 'author'], 'Agente')}</span>
                          <span className="text-[hsl(var(--muted-foreground))]">{fmtDate(pick(n, ['created_at', 'timestamp'], null))}</span>
                        </div>
                        <div className="text-sm whitespace-pre-wrap">{pick(n, ['note', 'body', 'text'], '')}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Acciones de agente */}
              {canAct && (
                <div className="space-y-4 pt-2 border-t border-border">
                  {lastSent && (
                    <div className="rounded-lg border border-emerald-500/30 bg-emerald-500/10 p-3 flex flex-wrap items-center justify-between gap-2" data-testid="last-sent-banner">
                      <span className="text-sm text-emerald-300">Respuesta enviada. ¿Sirve para futuros casos?</span>
                      <LearnButton message={lastSent.message} ticketId={ticketId(detail)} subject={pick(detail, ['subject', 'title'], '')} suggestionId={lastSent.sid} testid="ticket-learn-btn" />
                    </div>
                  )}
                  <AiSuggestBox ticketId={ticketId(detail)} onUse={(s) => { setReply(s.reply); setAiSid(s.id); }} />
                  <div>
                    <label className="text-sm font-medium flex items-center gap-1.5 mb-1"><Send className="w-4 h-4" />Responder al pastor</label>
                    {aiSid && <p className="text-xs text-[hsl(var(--primary))] mb-1" data-testid="reply-from-ai">Usando borrador de IA — puedes editarlo antes de enviar.</p>}
                    <Textarea value={reply} onChange={(e) => setReply(e.target.value)} rows={aiSid ? 7 : 3} placeholder="Escribe la respuesta que verá el pastor…" data-testid="reply-input" />
                    <div className="flex items-center justify-between mt-2">
                      <label className="flex items-center gap-2 text-sm cursor-pointer">
                        <Checkbox checked={closeOnReply} onCheckedChange={(v) => setCloseOnReply(!!v)} data-testid="reply-close" />
                        Cerrar el ticket al responder
                      </label>
                      <Button onClick={doRespond} disabled={busy || !reply.trim()} data-testid="reply-send"><Send className="w-4 h-4 mr-2" />Enviar</Button>
                    </div>
                  </div>
                  <div>
                    <label className="text-sm font-medium flex items-center gap-1.5 mb-1"><StickyNote className="w-4 h-4 text-amber-400" />Nota interna</label>
                    <Textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2} placeholder="Nota visible solo para el equipo…" data-testid="note-input" />
                    <div className="flex justify-end mt-2">
                      <Button variant="outline" onClick={doNote} disabled={busy || !note.trim()} data-testid="note-save">Guardar nota</Button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} data-testid="detail-close">Cerrar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
